import React, { useState, useEffect, useRef } from 'react';
import { ResponsiveRadar } from '@nivo/radar';
import GaugeChart from 'react-gauge-chart';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { toPng } from 'html-to-image';
import logoImage from '../Assets/Logo.png';
import { getApiBaseUrl } from '../config/api';
import './CultureAssessment.css';

const CultureAssessment = ({ organizationData }) => {
  const [questions, setQuestions] = useState([]);
  const [maturityStages, setMaturityStages] = useState([]);
  const [maturityStageLoading, setMaturityStageLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0); // 0 = maturity stage question, 1+ = culture questions
  const [responses, setResponses] = useState({});
  const [maturityStage, setMaturityStage] = useState(organizationData?.maturityStage || 'GROWTH');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactFormData, setContactFormData] = useState({ 
    email: organizationData?.email || '', 
    firstName: organizationData?.firstName || '', 
    lastName: organizationData?.lastName || '' 
  });
  const [contactFormError, setContactFormError] = useState('');
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [chartImages, setChartImages] = useState({ radar: null, gauges: {} });

  const questionsPerPage = 2;
  const totalPages = Math.ceil(questions.length / questionsPerPage); // Pages for culture questions only
  const previousPageRef = useRef(0);
  const wasAnsweredBeforeRef = useRef(new Set());
  
  useEffect(() => {
    fetchQuestions();
    fetchMaturityStages();
  }, []);

  // Auto-advance to next page when both questions on current page are answered
  useEffect(() => {
    // Only auto-advance if we're on a culture questions page (not page 0) and moving forward
    if (currentPage > 0 && currentPage < totalPages && questions.length > 0) {
      const startIndex = (currentPage - 1) * questionsPerPage;
      const endIndex = startIndex + questionsPerPage;
      const currentQuestions = questions.slice(startIndex, endIndex);
      const allAnswered = currentQuestions.every(q => responses[q.id] !== undefined);
      
      // Check if we're moving forward (not backward)
      const isMovingForward = currentPage > previousPageRef.current;
      // Check if this page was already answered before we arrived
      const pageKey = `page-${currentPage}`;
      const wasAlreadyAnswered = wasAnsweredBeforeRef.current.has(pageKey);
      
      // Only auto-advance if: all answered, moving forward, and wasn't already answered
      if (allAnswered && currentQuestions.length > 0 && isMovingForward && !wasAlreadyAnswered) {
        wasAnsweredBeforeRef.current.add(pageKey);
        // Delay to allow user to see their selection before transitioning
        const timer = setTimeout(() => {
          if (currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
          }
        }, 1000);
        
        return () => clearTimeout(timer);
      } else if (allAnswered && !wasAlreadyAnswered) {
        // Mark as answered even if we're not auto-advancing
        wasAnsweredBeforeRef.current.add(pageKey);
      }
      
      // Update previous page reference
      previousPageRef.current = currentPage;
    }
  }, [responses, currentPage, totalPages, questions, questionsPerPage]);

  // Capture charts as images when result is available
  useEffect(() => {
    if (completed && result) {
      const captureCharts = async () => {
        try {
          // Wait a bit for charts to fully render
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Capture radar chart
          const radarElement = document.getElementById('radar-chart-container');
          if (radarElement) {
            const radarImg = await toPng(radarElement, {
              quality: 1.0,
              pixelRatio: 2,
              backgroundColor: '#ffffff'
            });
            setChartImages(prev => ({ ...prev, radar: radarImg }));
          }

          // Capture each gauge chart
          if (result && result.scores) {
            const gaugeImages = {};
            for (const orientation of Object.keys(result.scores)) {
              const gaugeElement = document.getElementById(`gauge-chart-container-${orientation}`);
              if (gaugeElement) {
                try {
                  const gaugeImg = await toPng(gaugeElement, {
                    quality: 1.0,
                    pixelRatio: 2,
                    backgroundColor: '#ffffff'
                  });
                  gaugeImages[orientation] = gaugeImg;
                } catch (err) {
                  console.warn(`Failed to capture gauge chart for ${orientation}:`, err);
                }
              }
            }
            setChartImages(prev => ({ ...prev, gauges: gaugeImages }));
          }
        } catch (error) {
          console.error('Error capturing charts:', error);
        }
      };

      captureCharts();
    }
  }, [completed, result]);

  const fetchMaturityStages = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/maturity-stages`);
      const data = await response.json();
      
      if (data.success) {
        setMaturityStages(data.data);
      } else {
        console.error('Error fetching maturity stages:', data.message);
        setMaturityStages(getFallbackStages());
      }
    } catch (error) {
      console.error('Error fetching maturity stages:', error);
      setMaturityStages(getFallbackStages());
    } finally {
      setMaturityStageLoading(false);
    }
  };

  const getFallbackStages = () => [
    {
      code: 'STARTUP',
      label: 'Start-Up',
      description: 'Fast-growing, still setting structure and processes.'
    },
    {
      code: 'TURNAROUND',
      label: 'Turnaround',
      description: 'Regaining performance and focus after a difficult phase.'
    },
    {
      code: 'GROWTH',
      label: 'Accelerated Growth',
      description: 'Scaling quickly, adding people, markets, or products.'
    },
    {
      code: 'REALIGN',
      label: 'Realignment',
      description: 'Changing direction, structure, or leadership to stay relevant.'
    },
    {
      code: 'SUSTAIN',
      label: 'Sustaining Success',
      description: 'Established and stable; focused on innovation and renewal.'
    }
  ];

  const fetchQuestions = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/culture/questions`);
      const data = await response.json();
      
      if (data.success && Array.isArray(data.data)) {
        setQuestions(data.data);
        setResponses({});
      } else {
        console.error('Failed to fetch questions:', data.message);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMaturityStageChange = (stageCode) => {
    setMaturityStage(stageCode);
  };

  const handleScoreChange = (questionId, score) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: score
    }));
  };

  const handleNext = () => {
    // If on maturity stage page (page 0), require maturity stage to be selected
    if (currentPage === 0) {
      if (!maturityStage) {
        alert('Please select your organization type to continue.');
        return;
      }
    }
    
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleShowContactForm = () => {
    setShowContactForm(true);
  };

  const handleContactFormChange = (field, value) => {
    setContactFormData(prev => ({ ...prev, [field]: value }));
    setContactFormError('');
  };

  const handleSubmit = async (contactData = null) => {
    const email = contactData?.email || organizationData?.email || '';
    const firstName = contactData?.firstName || '';
    const lastName = contactData?.lastName || '';

    if (!email || !firstName) {
      setContactFormError('Please fill in email and first name.');
      return;
    }

    setSubmitting(true);
    
    try {
      // Convert responses to the format expected by the API
      const formattedResponses = Object.entries(responses).map(([questionId, score]) => {
        const question = questions.find(q => q.id === parseInt(questionId));
        return {
          questionId: parseInt(questionId),
          score: parseInt(score),
          orientation: question.orientation,
          subtheme: question.subtheme
        };
      });

      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/culture/results`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          responses: formattedResponses,
          organizationType: maturityStage || organizationData?.maturityStage || 'GROWTH',
          organizationName: organizationData?.organizationName || '',
          industry: organizationData?.industry || '',
          email,
          firstName,
          lastName,
          companySize: organizationData?.companySize || '',
          role: organizationData?.role || ''
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setResult(data.data);
        setCompleted(true);
      } else {
        console.error('Failed to submit assessment:', data.message);
        alert('Failed to submit assessment. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
      alert('Error submitting assessment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to format orientation name with "orientation" suffix
  const formatOrientationName = (orientation) => {
    return `${orientation} orientation`;
  };

  const getCultureHealthLabel = (score) => {
    if (score >= 85) return { label: 'Thriving', interpretation: 'Culture strongly supports strategy and results.', color: '#10b981' };
    if (score >= 70) return { label: 'Healthy', interpretation: 'Solid base, some areas to strengthen.', color: '#3b82f6' };
    if (score >= 55) return { label: 'Emerging', interpretation: 'Mixed culture signals; alignment needed.', color: '#f59e0b' };
    if (score >= 40) return { label: 'At-Risk', interpretation: 'Weak alignment; culture may hinder performance.', color: '#ef4444' };
    return { label: 'Misaligned', interpretation: 'Cultural breakdown; urgent attention required.', color: '#dc2626' };
  };

  const generateInterpretation = (cultureHealth, topStrength, keyImprovement, organizationType) => {
    const strengthText = `${formatOrientationName(topStrength.orientation)} (${Math.round(topStrength.score)}%)`;
    const improvementText = `${formatOrientationName(keyImprovement.orientation)} (${Math.round(keyImprovement.score)}%)`;
    
    const interpretations = {
      'Thriving': `Your organization demonstrates exceptional ${strengthText} capabilities, positioning you well for sustained success. Focus on maintaining this momentum while addressing ${improvementText} to achieve even greater organizational excellence.`,
      'Healthy': `Your organization shows strong ${strengthText} foundations that support your ${organizationType.toLowerCase()} objectives. To reach the next level, prioritize strengthening ${improvementText} through targeted initiatives and leadership focus.`,
      'Emerging': `Your organization has mixed signals with ${strengthText} showing promise but ${improvementText} requiring immediate attention. This suggests a need for clearer alignment and focused development efforts.`,
      'At-Risk': `Your organization faces significant challenges with ${improvementText} critically low, potentially hindering your ${organizationType.toLowerCase()} goals. Immediate intervention in ${improvementText} is essential for organizational health.`,
      'Misaligned': `Your organization requires urgent cultural transformation. While ${strengthText} shows some promise, ${improvementText} indicates fundamental misalignment that threatens organizational viability.`
    };
    
    return interpretations[cultureHealth.label] || interpretations['Healthy'];
  };

  // PDF Document Component
  const PDFDocument = ({ result, cultureHealth, interpretation, chartImages }) => {
    const styles = StyleSheet.create({
      page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 0,
        fontFamily: 'Helvetica',
      },
      header: {
        backgroundColor: '#9C4DF4',
        padding: 15,
        paddingBottom: 18,
        marginBottom: 0,
      },
      headerContent: {
        alignItems: 'center',
      },
      logoContainer: {
        marginBottom: 8,
        alignItems: 'center',
        backgroundColor: '#ffffff',
        padding: 8,
        borderRadius: 6,
      },
      logo: {
        width: 100,
        height: 'auto',
      },
      title: {
        fontSize: 18,
        fontWeight: 'bold',
        color: '#ffffff',
        marginBottom: 4,
        textAlign: 'center',
      },
      subtitle: {
        fontSize: 9,
        color: '#ffffff',
        opacity: 0.9,
        marginBottom: 2,
        textAlign: 'center',
      },
      content: {
        padding: 20,
        paddingTop: 18,
      },
      scoreSection: {
        marginBottom: 18,
        alignItems: 'center',
        paddingVertical: 15,
        backgroundColor: '#f9fafb',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#9C4DF4',
      },
      scoreCircle: {
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: cultureHealth.color,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 10,
        borderWidth: 2,
        borderColor: '#ffffff',
      },
      scoreText: {
        fontSize: 28,
        fontWeight: 'bold',
        color: '#ffffff',
      },
      scoreIndexText: {
        fontSize: 12,
        fontWeight: '600',
        color: '#111827',
        marginBottom: 4,
        textAlign: 'center',
      },
      healthLabel: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#9C4DF4',
        marginBottom: 5,
        textAlign: 'center',
      },
      healthInterpretation: {
        fontSize: 9,
        color: '#6b7280',
        textAlign: 'center',
        paddingHorizontal: 12,
        lineHeight: 1.4,
      },
      insightsSection: {
        marginBottom: 18,
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 10,
      },
      insightCard: {
        width: '48%',
        padding: 12,
        backgroundColor: '#ffffff',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#e5e7eb',
      },
      insightCardStrength: {
        borderLeftWidth: 3,
        borderLeftColor: '#3D9C9C',
      },
      insightCardOpportunity: {
        borderLeftWidth: 3,
        borderLeftColor: '#9C4DF4',
      },
      insightLabel: {
        fontSize: 8,
        color: '#6b7280',
        marginBottom: 4,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        fontWeight: '600',
      },
      insightTitle: {
        fontSize: 10,
        fontWeight: '600',
        color: '#111827',
        marginBottom: 6,
        lineHeight: 1.3,
      },
      insightScore: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#9C4DF4',
      },
      interpretationSection: {
        marginBottom: 18,
        padding: 14,
        backgroundColor: '#f9fafb',
        borderRadius: 6,
        borderLeftWidth: 3,
        borderLeftColor: '#9C4DF4',
      },
      sectionTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#111827',
        marginBottom: 7,
      },
      interpretationText: {
        fontSize: 9,
        color: '#374151',
        lineHeight: 1.5,
      },
      scoresSection: {
        marginBottom: 15,
        backgroundColor: '#ffffff',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#9C4DF4',
        padding: 12,
      },
      scoresTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#111827',
        marginBottom: 8,
        paddingBottom: 6,
        borderBottomWidth: 1,
        borderBottomColor: '#e5e7eb',
      },
      scoreRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 6,
        borderBottomWidth: 1,
        borderBottomColor: '#f3f4f6',
      },
      scoreLabel: {
        fontSize: 10,
        color: '#111827',
        fontWeight: '500',
        flex: 1,
      },
      scoreValue: {
        fontSize: 10,
        color: '#9C4DF4',
        fontWeight: 'bold',
        width: 35,
        textAlign: 'right',
      },
      footer: {
        marginTop: 'auto',
        paddingTop: 12,
        paddingHorizontal: 20,
        paddingBottom: 15,
        borderTopWidth: 1,
        borderTopColor: '#e5e7eb',
        backgroundColor: '#f9fafb',
      },
      footerText: {
        fontSize: 8,
        color: '#6b7280',
        textAlign: 'center',
        marginBottom: 2,
      },
      divider: {
        height: 1,
        backgroundColor: '#e5e7eb',
        marginVertical: 15,
      },
      chartSection: {
        marginBottom: 18,
        alignItems: 'center',
      },
      chartTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#111827',
        marginBottom: 10,
        textAlign: 'center',
      },
      chartImage: {
        width: '100%',
        maxWidth: 500,
        height: 'auto',
        marginBottom: 12,
      },
      gaugeGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        gap: 8,
        marginTop: 10,
      },
      gaugeCard: {
        width: '48%',
        marginBottom: 12,
        alignItems: 'center',
      },
      gaugeTitle: {
        fontSize: 9,
        fontWeight: '600',
        color: '#111827',
        marginBottom: 6,
        textAlign: 'center',
      },
      gaugeImage: {
        width: '100%',
        maxWidth: 150,
        height: 'auto',
      },
      toxicitySection: {
        marginBottom: 15,
        backgroundColor: '#fef2f2',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#fecaca',
        padding: 12,
      },
      toxicityTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#dc2626',
        marginBottom: 8,
        paddingBottom: 6,
        borderBottomWidth: 1,
        borderBottomColor: '#fecaca',
      },
      toxicityDescription: {
        fontSize: 8,
        color: '#6b7280',
        marginBottom: 10,
        lineHeight: 1.4,
      },
      toxicityIndexCard: {
        backgroundColor: '#ffffff',
        borderRadius: 6,
        padding: 12,
        marginBottom: 10,
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#fecaca',
      },
      toxicityIndexLabel: {
        fontSize: 10,
        fontWeight: '600',
        color: '#111827',
        marginBottom: 6,
      },
      toxicityIndexValue: {
        fontSize: 28,
        fontWeight: 'bold',
        color: '#dc2626',
        marginBottom: 4,
      },
      toxicityIndexSubtitle: {
        fontSize: 8,
        color: '#6b7280',
      },
      toxicityRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 6,
        borderBottomWidth: 1,
        borderBottomColor: '#fef2f2',
      },
      toxicityLabel: {
        fontSize: 10,
        color: '#111827',
        fontWeight: '500',
        flex: 1,
      },
      toxicityValue: {
        fontSize: 10,
        color: '#dc2626',
        fontWeight: 'bold',
        width: 35,
        textAlign: 'right',
      },
      dualIndexSection: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 18,
      },
      indexCard: {
        flex: 1,
        backgroundColor: '#f9fafb',
        borderRadius: 8,
        padding: 16,
        alignItems: 'center',
        borderWidth: 2,
      },
      healthIndexCard: {
        borderColor: '#9C4DF4',
      },
      toxicityIndexCardPDF: {
        borderColor: '#dc2626',
        backgroundColor: '#fef2f2',
      },
      indexCardTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        color: '#111827',
        marginBottom: 12,
        textAlign: 'center',
      },
      indexCircle: {
        width: 70,
        height: 70,
        borderRadius: 35,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 10,
        borderWidth: 3,
        borderColor: '#ffffff',
      },
      healthIndexCircle: {
        backgroundColor: '#9C4DF4',
      },
      toxicityIndexCircle: {
        backgroundColor: '#dc2626',
      },
      indexValue: {
        fontSize: 22,
        fontWeight: 'bold',
        color: '#ffffff',
      },
      indexSmallLabel: {
        fontSize: 10,
        fontWeight: '600',
        color: '#ffffff',
      },
      indexStatus: {
        fontSize: 10,
        fontWeight: 'bold',
        marginBottom: 4,
        textAlign: 'center',
      },
      healthStatus: {
        color: '#9C4DF4',
      },
      toxicityStatus: {
        color: '#dc2626',
      },
      indexDescription: {
        fontSize: 8,
        color: '#6b7280',
        textAlign: 'center',
        lineHeight: 1.3,
      },
      narrativeSectionPDF: {
        marginBottom: 15,
        padding: 12,
        backgroundColor: '#f9fafb',
        borderRadius: 6,
        borderWidth: 1,
        borderColor: '#9C4DF4',
      },
      narrativeTitlePDF: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#111827',
        marginBottom: 8,
        paddingBottom: 6,
        borderBottomWidth: 1,
        borderBottomColor: '#e5e7eb',
      },
      narrativeSummaryPDF: {
        fontSize: 10,
        fontWeight: '600',
        color: '#111827',
        marginBottom: 8,
        lineHeight: 1.4,
      },
      narrativeDetailPDF: {
        marginBottom: 8,
      },
      narrativeLabelPDF: {
        fontSize: 8,
        fontWeight: 'bold',
        color: '#6b7280',
        marginBottom: 3,
        textTransform: 'uppercase',
      },
      narrativeTextPDF: {
        fontSize: 9,
        color: '#374151',
        lineHeight: 1.4,
      },
      narrativeActionPDF: {
        fontSize: 9,
        color: '#111827',
        marginLeft: 8,
        marginBottom: 3,
        lineHeight: 1.4,
      },
      orientationNarrativePDF: {
        marginBottom: 12,
        padding: 10,
        backgroundColor: '#f9fafb',
        borderRadius: 6,
        borderLeftWidth: 3,
        borderLeftColor: '#3b82f6',
      },
      orientationTitlePDF: {
        fontSize: 11,
        fontWeight: 'bold',
        color: '#111827',
        marginBottom: 5,
      },
      orientationMeaningPDF: {
        fontSize: 9,
        color: '#374151',
        marginBottom: 6,
        lineHeight: 1.3,
      },
      signalBadgePDF: {
        backgroundColor: '#dbeafe',
        padding: 4,
        paddingHorizontal: 8,
        borderRadius: 4,
        marginRight: 4,
        marginBottom: 4,
      },
      signalTextPDF: {
        fontSize: 8,
        color: '#1e40af',
        fontWeight: '500',
      },
      contextModifierPDF: {
        fontSize: 9,
        color: '#111827',
        marginLeft: 8,
        marginBottom: 4,
        padding: 6,
        backgroundColor: '#fffbeb',
        borderLeftWidth: 2,
        borderLeftColor: '#f59e0b',
        borderRadius: 4,
        lineHeight: 1.4,
      },
      edgeCaseNotePDF: {
        fontSize: 9,
        fontWeight: '600',
        color: '#dc2626',
        marginLeft: 8,
        marginBottom: 4,
        padding: 6,
        backgroundColor: '#fef2f2',
        borderLeftWidth: 2,
        borderLeftColor: '#ef4444',
        borderRadius: 4,
        lineHeight: 1.4,
      },
      interpretationTableSection: {
        marginBottom: 15,
        backgroundColor: '#f9fafb',
        borderRadius: 8,
        padding: 12,
        borderWidth: 2,
      },
      healthInterpretationTable: {
        borderColor: '#9C4DF4',
      },
      toxicityInterpretationTable: {
        borderColor: '#dc2626',
        backgroundColor: '#fef2f2',
      },
      interpretationTableTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        color: '#111827',
        marginBottom: 10,
        paddingBottom: 6,
        borderBottomWidth: 1,
        borderBottomColor: '#e5e7eb',
      },
      interpretationTableHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
        paddingBottom: 8,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(0, 0, 0, 0.1)',
      },
      interpretationRange: {
        fontSize: 9,
        fontWeight: '600',
        color: '#6b7280',
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        padding: 3,
        paddingHorizontal: 8,
        borderRadius: 4,
      },
      interpretationCategory: {
        fontSize: 10,
        fontWeight: 'bold',
      },
      interpretationRow: {
        marginBottom: 8,
      },
      interpretationLabel: {
        fontSize: 8,
        fontWeight: '600',
        color: '#374151',
        marginBottom: 3,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
      },
      interpretationText: {
        fontSize: 9,
        color: '#111827',
        lineHeight: 1.4,
      },
    });

    return (
      <Document>
        <Page size="A4" style={styles.page}>
          {/* Header with Purple Background */}
          <View style={styles.header}>
            <View style={styles.headerContent}>
              <View style={styles.logoContainer}>
                <Image src={logoImage} style={styles.logo} />
              </View>
              <Text style={styles.title}>Culture Health Assessment Report</Text>
              <Text style={styles.subtitle}>Assessment Completed: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</Text>
              {result.organizationName && (
                <Text style={styles.subtitle}>Organization: {result.organizationName}</Text>
              )}
            </View>
          </View>

          {/* Content Section */}
          <View style={styles.content}>
            {/* Dual Index Cards - Health and Toxicity Side by Side */}
            <View style={styles.dualIndexSection}>
              {/* Culture Health Index Card */}
              <View style={[styles.indexCard, styles.healthIndexCard]}>
                <Text style={styles.indexCardTitle}>Culture Health Index</Text>
                <View style={[styles.indexCircle, styles.healthIndexCircle]}>
                  <Text style={styles.indexValue}>{Math.round(result.cultureIndex)}</Text>
                  <Text style={styles.indexSmallLabel}>%</Text>
                </View>
                <Text style={[styles.indexStatus, styles.healthStatus]}>{cultureHealth.label}</Text>
                <Text style={styles.indexDescription}>{cultureHealth.interpretation}</Text>
              </View>

              {/* Culture Toxicity Index Card */}
              {result.toxicityData && (
                <View style={[styles.indexCard, styles.toxicityIndexCardPDF]}>
                  <Text style={styles.indexCardTitle}>Culture Toxicity Index</Text>
                  <View style={[styles.indexCircle, styles.toxicityIndexCircle]}>
                    <Text style={styles.indexValue}>{Math.round(result.toxicityData.cultureToxicityIndex * 10)}</Text>
                    <Text style={styles.indexSmallLabel}>%</Text>
                  </View>
                  <Text style={[styles.indexStatus, styles.toxicityStatus]}>
                    {result.toxicityData.cultureToxicityIndex <= 3 ? 'Low Toxicity' : 
                     result.toxicityData.cultureToxicityIndex <= 6 ? 'Moderate Toxicity' : 
                     'High Toxicity'}
                  </Text>
                  <Text style={styles.indexDescription}>Lower toxicity indicates healthier culture</Text>
                </View>
              )}
            </View>

            {/* Interpretation Tables */}
            {result.cultureHealthDetails && (
              <View style={[styles.interpretationTableSection, styles.healthInterpretationTable]}>
                <Text style={styles.interpretationTableTitle}>Culture Health Index (CHI) Interpretation</Text>
                <View style={styles.interpretationTableHeader}>
                  <Text style={styles.interpretationRange}>{result.cultureHealthDetails.range}</Text>
                  <Text style={[styles.interpretationCategory, { color: result.cultureHealthDetails.color }]}>
                    {result.cultureHealthDetails.category}
                  </Text>
                </View>
                <View style={styles.interpretationRow}>
                  <Text style={styles.interpretationLabel}>Meaning:</Text>
                  <Text style={styles.interpretationText}>{result.cultureHealthDetails.meaning}</Text>
                </View>
                <View style={styles.interpretationRow}>
                  <Text style={styles.interpretationLabel}>Focus:</Text>
                  <Text style={styles.interpretationText}>{result.cultureHealthDetails.focus}</Text>
                </View>
              </View>
            )}

            {result.toxicityData?.toxicityDetails && (
              <View style={[styles.interpretationTableSection, styles.toxicityInterpretationTable]}>
                <Text style={styles.interpretationTableTitle}>Culture Toxicity Index (CTI) Interpretation</Text>
                <View style={styles.interpretationTableHeader}>
                  <Text style={styles.interpretationRange}>{result.toxicityData.toxicityDetails.range}</Text>
                  <Text style={[styles.interpretationCategory, { color: result.toxicityData.toxicityDetails.color }]}>
                    {result.toxicityData.toxicityDetails.category}
                  </Text>
                </View>
                <View style={styles.interpretationRow}>
                  <Text style={styles.interpretationLabel}>Meaning:</Text>
                  <Text style={styles.interpretationText}>{result.toxicityData.toxicityDetails.meaning}</Text>
                </View>
                <View style={styles.interpretationRow}>
                  <Text style={styles.interpretationLabel}>Focus:</Text>
                  <Text style={styles.interpretationText}>{result.toxicityData.toxicityDetails.focus}</Text>
                </View>
              </View>
            )}

            {/* Insights Cards */}
            <View style={styles.insightsSection}>
              <View style={[styles.insightCard, styles.insightCardStrength]}>
                <Text style={styles.insightLabel}>Top Strength</Text>
                <Text style={styles.insightTitle}>{formatOrientationName(result.topStrength.orientation)}</Text>
                <Text style={styles.insightScore}>{Math.round(result.topStrength.score)}%</Text>
              </View>
              <View style={[styles.insightCard, styles.insightCardOpportunity]}>
                <Text style={styles.insightLabel}>Key Opportunity</Text>
                <Text style={styles.insightTitle}>{formatOrientationName(result.keyImprovement.orientation)}</Text>
                <Text style={styles.insightScore}>{Math.round(result.keyImprovement.score)}%</Text>
              </View>
            </View>

            {/* Interpretation */}
            <View style={styles.interpretationSection}>
              <Text style={styles.sectionTitle}>What This Means</Text>
              <Text style={styles.interpretationText}>{interpretation}</Text>
            </View>

            {/* Radar Chart */}
            {chartImages && chartImages.radar && (
              <View style={styles.chartSection}>
                <Text style={styles.chartTitle}>Culture Orientation Breakdown</Text>
                <Image src={chartImages.radar} style={styles.chartImage} />
              </View>
            )}
          </View>

          {/* Footer for first page */}
          <View style={styles.footer}>
            <Text style={styles.footerText}>Generated by Culture Health Check Assessment</Text>
            <Text style={styles.footerText}>© {new Date().getFullYear()} Enculture. All rights reserved.</Text>
          </View>
        </Page>

        {/* Second Page - Detailed Scores and Gauge Charts */}
        <Page size="A4" style={styles.page}>
          <View style={styles.content}>
            {/* Detailed Scores */}
            <View style={styles.scoresSection}>
              <Text style={styles.scoresTitle}>Detailed Scores</Text>
              {Object.entries(result.scores).map(([orientation, score]) => (
                <View key={orientation} style={styles.scoreRow}>
                  <Text style={styles.scoreLabel}>{formatOrientationName(orientation)}</Text>
                  <Text style={styles.scoreValue}>{Math.round(score)}%</Text>
                </View>
              ))}
            </View>

            {/* Gauge Charts */}
            {chartImages && chartImages.gauges && Object.keys(chartImages.gauges).length > 0 && (
              <View style={styles.chartSection}>
                <Text style={styles.chartTitle}>Orientation Score Visualization</Text>
                <View style={styles.gaugeGrid}>
                  {Object.entries(result.scores).map(([orientation, score]) => (
                    chartImages.gauges[orientation] && (
                      <View key={orientation} style={styles.gaugeCard}>
                        <Text style={styles.gaugeTitle}>{formatOrientationName(orientation)}</Text>
                        <Image src={chartImages.gauges[orientation]} style={styles.gaugeImage} />
                      </View>
                    )
                  ))}
                </View>
              </View>
            )}

            {/* Toxicity Analysis */}
            {result.toxicityData && (
              <View style={styles.toxicitySection}>
                <Text style={styles.toxicityTitle}>Culture Toxicity Analysis</Text>
                <Text style={styles.toxicityDescription}>
                  Toxicity measures the gap between ideal (10) and actual ratings. Lower toxicity indicates healthier culture.
                </Text>
                
                {/* Culture Toxicity Index */}
                <View style={styles.toxicityIndexCard}>
                  <Text style={styles.toxicityIndexLabel}>Culture Toxicity Index</Text>
                  <Text style={styles.toxicityIndexValue}>
                    {Math.round(result.toxicityData.cultureToxicityIndex * 10)}%
                  </Text>
                  <Text style={styles.toxicityIndexSubtitle}>Lower is healthier</Text>
                </View>

                {/* Orientation Toxicity Scores */}
                <View style={{ marginTop: 10 }}>
                  <Text style={[styles.sectionTitle, { marginBottom: 6 }]}>Toxicity by Orientation</Text>
                  {Object.entries(result.toxicityData.orientationToxicities).map(([orientation, toxicity]) => (
                    <View key={orientation} style={styles.toxicityRow}>
                      <Text style={styles.toxicityLabel}>{formatOrientationName(orientation)}</Text>
                      <Text style={styles.toxicityValue}>{Math.round(toxicity * 10)}%</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
          </View>

          {/* Footer for second page */}
          <View style={styles.footer}>
            <Text style={styles.footerText}>Generated by Culture Health Check Assessment</Text>
            <Text style={styles.footerText}>© {new Date().getFullYear()} Enculture. All rights reserved.</Text>
          </View>
        </Page>

        {/* Page 3: Key Insights - matching UI layout */}
        <Page size="A4" style={styles.page}>
          <View style={styles.content}>
            {/* Key Insights Header */}
            <Text style={[styles.sectionTitle, { marginBottom: 15, fontSize: 14 }]}>Key Insights</Text>
            
            {/* Insights Grid */}
            <View style={styles.insightsSection}>
              <View style={[styles.insightCard, styles.insightCardStrength]}>
                <Text style={styles.insightLabel}>Top Strength</Text>
                <Text style={styles.insightTitle}>{formatOrientationName(result.topStrength.orientation)}</Text>
                <Text style={styles.insightScore}>{Math.round(result.topStrength.score)}%</Text>
              </View>
              <View style={[styles.insightCard, styles.insightCardOpportunity]}>
                <Text style={styles.insightLabel}>Key Opportunity</Text>
                <Text style={styles.insightTitle}>{formatOrientationName(result.keyImprovement.orientation)}</Text>
                <Text style={styles.insightScore}>{Math.round(result.keyImprovement.score)}%</Text>
              </View>
            </View>

            {/* Comprehensive Narratives - following UI order */}
            {result.narratives && (
              <>
                {/* Culture Health Narrative */}
                <View style={styles.narrativeSectionPDF}>
                  <Text style={styles.narrativeTitlePDF}>Culture Health Analysis</Text>
                  <Text style={styles.narrativeSummaryPDF}>{result.narratives.chi.summary}</Text>
                  
                  <View style={styles.narrativeDetailPDF}>
                    <Text style={styles.narrativeLabelPDF}>Why It Matters:</Text>
                    <Text style={styles.narrativeTextPDF}>{result.narratives.chi.why_it_matters}</Text>
                  </View>
                  
                  <View style={styles.narrativeDetailPDF}>
                    <Text style={styles.narrativeLabelPDF}>Leadership Note:</Text>
                    <Text style={styles.narrativeTextPDF}>{result.narratives.chi.leadership_note}</Text>
                  </View>
                  
                  <View style={styles.narrativeDetailPDF}>
                    <Text style={styles.narrativeLabelPDF}>90-Day Actions:</Text>
                    {result.narratives.chi.selectedActions.map((action, idx) => (
                      <Text key={idx} style={styles.narrativeActionPDF}>✓ {action}</Text>
                    ))}
                  </View>
                </View>

                {/* Toxicity Narrative */}
                <View style={[styles.narrativeSectionPDF, { backgroundColor: '#fef2f2', borderColor: '#fecaca' }]}>
                  <Text style={[styles.narrativeTitlePDF, { color: '#dc2626' }]}>Toxicity Analysis</Text>
                  <Text style={styles.narrativeSummaryPDF}>{result.narratives.cti.summary}</Text>
                  
                  <View style={styles.narrativeDetailPDF}>
                    <Text style={styles.narrativeLabelPDF}>Risk Watch:</Text>
                    <Text style={styles.narrativeTextPDF}>{result.narratives.cti.risk_watch}</Text>
                  </View>
                  
                  <View style={styles.narrativeDetailPDF}>
                    <Text style={styles.narrativeLabelPDF}>90-Day Actions:</Text>
                    {result.narratives.cti.selectedActions.map((action, idx) => (
                      <Text key={idx} style={styles.narrativeActionPDF}>✓ {action}</Text>
                    ))}
                  </View>
                </View>
              </>
            )}
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Text style={styles.footerText}>Generated by Culture Health Check Assessment</Text>
            <Text style={styles.footerText}>© {new Date().getFullYear()} Enculture. All rights reserved.</Text>
          </View>
        </Page>

        {/* Page 4: Orientation Strengths - matching UI layout */}
        {result.narratives && (
          <Page size="A4" style={styles.page}>
            <View style={styles.content}>
              <Text style={[styles.sectionTitle, { marginBottom: 12, fontSize: 14 }]}>Your Cultural Strengths</Text>
              {result.narratives.orientations.strengths.map((orient, idx) => (
                <View key={idx} style={styles.orientationNarrativePDF}>
                  <Text style={styles.orientationTitlePDF}>{orient.name}</Text>
                  <Text style={styles.orientationMeaningPDF}>{orient.meaning}</Text>
                  
                  <View style={{ marginTop: 6 }}>
                    <Text style={styles.narrativeLabelPDF}>Signals:</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 4 }}>
                      {orient.signals.map((signal, sidx) => (
                        <View key={sidx} style={styles.signalBadgePDF}>
                          <Text style={styles.signalTextPDF}>{signal}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                  
                  <View style={{ marginTop: 8 }}>
                    <Text style={styles.narrativeLabelPDF}>Actions to Sustain:</Text>
                    {orient.selectedActions.map((action, aidx) => (
                      <Text key={aidx} style={styles.narrativeActionPDF}>→ {action}</Text>
                    ))}
                  </View>
                </View>
              ))}
            </View>

            {/* Footer */}
            <View style={styles.footer}>
              <Text style={styles.footerText}>Generated by Culture Health Check Assessment</Text>
              <Text style={styles.footerText}>© {new Date().getFullYear()} Enculture. All rights reserved.</Text>
            </View>
          </Page>
        )}

        {/* Page 5: Focus Areas - matching UI layout */}
        {result.narratives && (
          <Page size="A4" style={styles.page}>
            <View style={styles.content}>
              <Text style={[styles.sectionTitle, { marginBottom: 12, fontSize: 14 }]}>Focus Areas for Improvement</Text>
              {result.narratives.orientations.focus.map((orient, idx) => (
                <View key={idx} style={[styles.orientationNarrativePDF, { backgroundColor: '#fffbeb', borderLeftColor: '#f59e0b' }]}>
                  <Text style={styles.orientationTitlePDF}>{orient.name}</Text>
                  <Text style={styles.orientationMeaningPDF}>{orient.meaning}</Text>
                  
                  <View style={{ marginTop: 6 }}>
                    <Text style={styles.narrativeLabelPDF}>Signals:</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 4 }}>
                      {orient.signals.map((signal, sidx) => (
                        <View key={sidx} style={[styles.signalBadgePDF, { backgroundColor: '#fef3c7' }]}>
                          <Text style={[styles.signalTextPDF, { color: '#92400e' }]}>{signal}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                  
                  <View style={{ marginTop: 8 }}>
                    <Text style={styles.narrativeLabelPDF}>Actions to Improve:</Text>
                    {orient.selectedActions.map((action, aidx) => (
                      <Text key={aidx} style={styles.narrativeActionPDF}>→ {action}</Text>
                    ))}
                  </View>
                </View>
              ))}

              {/* Context Modifiers */}
              {result.narratives.contextModifiers && result.narratives.contextModifiers.length > 0 && (
                <View style={{ marginTop: 15 }}>
                  <Text style={[styles.sectionTitle, { marginBottom: 8, fontSize: 12 }]}>Context-Specific Guidance</Text>
                  {result.narratives.contextModifiers.map((modifier, idx) => (
                    <Text key={idx} style={styles.contextModifierPDF}>• {modifier}</Text>
                  ))}
                </View>
              )}

              {/* Edge Case Notes */}
              {result.narratives.edgeCaseNotes && result.narratives.edgeCaseNotes.length > 0 && (
                <View style={{ marginTop: 15 }}>
                  <Text style={[styles.sectionTitle, { marginBottom: 8, fontSize: 12, color: '#dc2626' }]}>Important Notes</Text>
                  {result.narratives.edgeCaseNotes.map((note, idx) => (
                    <Text key={idx} style={styles.edgeCaseNotePDF}>⚠ {note}</Text>
                  ))}
                </View>
              )}
            </View>

            {/* Footer */}
            <View style={styles.footer}>
              <Text style={styles.footerText}>Generated by Culture Health Check Assessment</Text>
              <Text style={styles.footerText}>© {new Date().getFullYear()} Enculture. All rights reserved.</Text>
            </View>
          </Page>
        )}
      </Document>
    );
  };

  const getCurrentQuestions = () => {
    // Page 0 is maturity stage question, pages 1+ are culture questions
    if (currentPage === 0) return [];
    const startIndex = (currentPage - 1) * questionsPerPage;
    const endIndex = startIndex + questionsPerPage;
    return questions.slice(startIndex, endIndex);
  };


  const isCurrentPageComplete = () => {
    if (currentPage === 0) {
      return !!maturityStage;
    }
    const currentQuestions = getCurrentQuestions();
    return currentQuestions.every(q => responses[q.id] !== undefined);
  };

  const isAllComplete = () => {
    // Check maturity stage is selected
    if (!maturityStage) return false;
    // Check all culture questions are answered
    return questions.every(q => responses[q.id] !== undefined);
  };

  if (loading) {
    return (
      <div className="culture-assessment">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading assessment questions...</p>
        </div>
      </div>
    );
  }


  if (completed && result) {
    const cultureHealth = getCultureHealthLabel(Math.round(result.cultureIndex));
    const interpretation = generateInterpretation(cultureHealth, result.topStrength, result.keyImprovement, result.organizationType);
    
    return (
      <div className="culture-assessment">
        <div className="results-container" id="results-container">
          {/* Hero Section */}
          <div className="results-hero">
            <div className="results-hero-backdrop" aria-hidden="true">
              <span className="hero-orb orb-one"></span>
              <span className="hero-orb orb-two"></span>
              <span className="hero-grid"></span>
            </div>
            <div className="results-hero-card">
              <span className="success-icon-pill" aria-hidden="true">
                <svg viewBox="0 0 72 72" role="img" aria-label="Success icon">
                  <defs>
                    <linearGradient id="successIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#9C4DF4" />
                      <stop offset="100%" stopColor="#38BFD9" />
                    </linearGradient>
                  </defs>
                  <rect x="2" y="2" width="68" height="68" rx="20" fill="rgba(255,255,255,0.3)" />
                  <rect x="6" y="6" width="60" height="60" rx="18" fill="url(#successIconGradient)" />
                  <path d="M26 37l8 8 14-18" fill="none" stroke="#fff" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <h1 className="results-title">Assessment Complete!</h1>
              <p className="results-subtitle">Your culture health snapshot is ready.</p>
              <p className="results-value-note">Get your personalized insights report.</p>
              <PDFDownloadLink
                document={<PDFDocument result={result} cultureHealth={cultureHealth} interpretation={interpretation} chartImages={chartImages} />}
                fileName={`Culture_Health_Report_${result.organizationName ? result.organizationName.replace(/\s+/g, '_') : 'CultureAssessment'}_${new Date().toISOString().split('T')[0]}.pdf`}
                className="download-pdf-btn"
              >
                {({ loading }) => (loading ? 'Generating PDF...' : 'Download Report')}
              </PDFDownloadLink>
              <span className="cta-note">Your data is private and secure.</span>
            </div>
          </div>

          {/* Dual Index Cards - Health and Toxicity Side by Side */}
          <div className="dual-index-container">
            {/* Culture Health Index Card */}
            <div className="index-card health-index-card">
              <div className="index-card-header">
                <h3 className="index-card-title">Culture Health Index (CHI)</h3>
              </div>
              <div className="index-circle-modern health-circle" style={{ 
                background: `linear-gradient(135deg, ${cultureHealth.color}, ${cultureHealth.color}dd)`,
                boxShadow: `0 8px 32px ${cultureHealth.color}40`
              }}>
                <span className="index-value-modern">{Math.round(result.cultureIndex)}<span className="index-label-modern">%</span></span>
              </div>
              <div className="index-card-content">
                {result.cultureHealthDetails ? (
                  <>
                    <div className="index-category-header">
                      <span className="index-range-badge">{result.cultureHealthDetails.range}</span>
                      <p className="index-category" style={{ color: result.cultureHealthDetails.color }}>
                        <strong>{result.cultureHealthDetails.category}</strong>
                      </p>
                    </div>
                    <div className="index-details">
                      <div className="index-detail-row">
                        <span className="detail-label">Meaning:</span>
                        <p className="detail-text">{result.cultureHealthDetails.meaning}</p>
                      </div>
                      <div className="index-detail-row">
                        <span className="detail-label">Focus:</span>
                        <p className="detail-text">{result.cultureHealthDetails.focus}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="index-description" style={{ color: cultureHealth.color }}>
                      <strong>{cultureHealth.label}</strong>
                    </p>
                    <p className="index-interpretation">{cultureHealth.interpretation}</p>
                  </>
                )}
              </div>
            </div>

            {/* Culture Toxicity Index Card */}
            {result.toxicityData && (
              <div className="index-card toxicity-index-card">
                <div className="index-card-header">
                  <h3 className="index-card-title">Culture Toxicity Index (CTI)</h3>
                </div>
                <div className="index-circle-modern toxicity-circle">
                  <span className="index-value-modern">{Math.round(result.toxicityData.cultureToxicityIndex * 10)}<span className="index-label-modern">%</span></span>
                </div>
                <div className="index-card-content">
                  {result.toxicityData.toxicityDetails ? (
                    <>
                      <div className="index-category-header">
                        <span className="index-range-badge">{result.toxicityData.toxicityDetails.range}</span>
                        <p className="index-category" style={{ color: result.toxicityData.toxicityDetails.color }}>
                          <strong>{result.toxicityData.toxicityDetails.category}</strong>
                        </p>
                      </div>
                      <div className="index-details">
                        <div className="index-detail-row">
                          <span className="detail-label">Meaning:</span>
                          <p className="detail-text">{result.toxicityData.toxicityDetails.meaning}</p>
                        </div>
                        <div className="index-detail-row">
                          <span className="detail-label">Focus:</span>
                          <p className="detail-text">{result.toxicityData.toxicityDetails.focus}</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="index-description" style={{ color: '#dc2626' }}>
                        <strong>
                          {result.toxicityData.cultureToxicityIndex <= 3 ? 'Low Toxicity' : 
                           result.toxicityData.cultureToxicityIndex <= 6 ? 'Moderate Toxicity' : 
                           'High Toxicity'}
                        </strong>
                      </p>
                      <p className="index-interpretation">
                        Toxicity measures the gap between ideal and actual ratings. Lower toxicity indicates healthier culture.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Key Insights */}
          <div className="insights-grid">
            <div className="insight-card strength-card">
              <div className="insight-icon strength-icon">1</div>
              <div className="insight-content">
                <span className="insight-label">Top Strength</span>
                <h3 className="insight-title">{formatOrientationName(result.topStrength.orientation)}</h3>
                <div className="insight-score">{Math.round(result.topStrength.score)}%</div>
              </div>
            </div>
            <div className="insight-card improvement-card">
              <div className="insight-icon improvement-icon">2</div>
              <div className="insight-content">
                <span className="insight-label">Key Opportunity</span>
                <h3 className="insight-title">{formatOrientationName(result.keyImprovement.orientation)}</h3>
                <div className="insight-score">{Math.round(result.keyImprovement.score)}%</div>
              </div>
            </div>
          </div>

          {/* Comprehensive Narratives Section */}
          {result.narratives && (
            <div className="comprehensive-narratives">
              {/* Culture Health Narrative */}
              <div className="narrative-section chi-narrative">
                <h3 className="narrative-title">Culture Health Analysis</h3>
                <div className="narrative-content">
                  <p className="narrative-summary">{result.narratives.chi.summary}</p>
                  <div className="narrative-detail">
                    <strong>Why It Matters:</strong>
                    <p>{result.narratives.chi.why_it_matters}</p>
                  </div>
                  <div className="narrative-detail">
                    <strong>Leadership Note:</strong>
                    <p>{result.narratives.chi.leadership_note}</p>
                  </div>
                  <div className="narrative-actions">
                    <strong>90-Day Actions:</strong>
                    <ul>
                      {result.narratives.chi.selectedActions.map((action, idx) => (
                        <li key={idx}>{action}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Culture Toxicity Narrative */}
              <div className="narrative-section cti-narrative">
                <h3 className="narrative-title">Toxicity Analysis</h3>
                <div className="narrative-content">
                  <p className="narrative-summary">{result.narratives.cti.summary}</p>
                  <div className="narrative-detail">
                    <strong>Risk Watch:</strong>
                    <p>{result.narratives.cti.risk_watch}</p>
                  </div>
                  <div className="narrative-actions">
                    <strong>90-Day Actions:</strong>
                    <ul>
                      {result.narratives.cti.selectedActions.map((action, idx) => (
                        <li key={idx}>{action}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Orientation Strengths */}
              <div className="narrative-section orientations-narrative">
                <h3 className="narrative-title">Your Cultural Strengths</h3>
                {result.narratives.orientations.strengths.map((orient, idx) => (
                  <div key={idx} className="orientation-narrative-card strength-narrative">
                    <h4 className="orientation-narrative-title">{orient.name}</h4>
                    <p className="orientation-meaning">{orient.meaning}</p>
                    <div className="orientation-signals">
                      <strong>Signals:</strong>
                      <ul>
                        {orient.signals.map((signal, sidx) => (
                          <li key={sidx}>{signal}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="orientation-actions">
                      <strong>Actions to Sustain:</strong>
                      <ul>
                        {orient.selectedActions.map((action, aidx) => (
                          <li key={aidx}>{action}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>

              {/* Orientation Focus Areas */}
              <div className="narrative-section orientations-narrative">
                <h3 className="narrative-title">Focus Areas for Improvement</h3>
                {result.narratives.orientations.focus.map((orient, idx) => (
                  <div key={idx} className="orientation-narrative-card focus-narrative">
                    <h4 className="orientation-narrative-title">{orient.name}</h4>
                    <p className="orientation-meaning">{orient.meaning}</p>
                    <div className="orientation-signals">
                      <strong>Signals:</strong>
                      <ul>
                        {orient.signals.map((signal, sidx) => (
                          <li key={sidx}>{signal}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="orientation-actions">
                      <strong>Actions to Improve:</strong>
                      <ul>
                        {orient.selectedActions.map((action, aidx) => (
                          <li key={aidx}>{action}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>

              {/* Context Modifiers */}
              {result.narratives.contextModifiers && result.narratives.contextModifiers.length > 0 && (
                <div className="narrative-section context-narrative">
                  <h3 className="narrative-title">Context-Specific Guidance</h3>
                  <ul className="context-list">
                    {result.narratives.contextModifiers.map((modifier, idx) => (
                      <li key={idx}>{modifier}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Edge Case Notes */}
              {result.narratives.edgeCaseNotes && result.narratives.edgeCaseNotes.length > 0 && (
                <div className="narrative-section edge-case-narrative">
                  <h3 className="narrative-title">Important Notes</h3>
                  <ul className="edge-case-list">
                    {result.narratives.edgeCaseNotes.map((note, idx) => (
                      <li key={idx}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Fallback Interpretation (if narratives not available) */}
          {!result.narratives && (
            <div className="interpretation-card">
              <h3 className="section-title">What This Means</h3>
              <p className="interpretation-text-modern">{interpretation}</p>
            </div>
          )}

          {/* Radar Chart */}
          <div className="radar-section-modern">
            <h3 className="section-title">Culture Orientation Breakdown</h3>
            <div id="radar-chart-container" className="radar-container-modern">
              <ResponsiveRadar
                data={Object.entries(result.scores).map(([orientation, score]) => ({
                  orientation: formatOrientationName(orientation),
                  score: Math.round(score),
                  fullMark: 100
                }))}
                keys={['score']}
                indexBy="orientation"
                maxValue={100}
                margin={{ top: 70, right: 80, bottom: 40, left: 80 }}
                curve="linearClosed"
                borderWidth={3}
                borderColor={{ from: 'color', modifiers: [['darker', 1]] }}
                gridLevels={5}
                gridShape="circular"
                gridLabelOffset={20}
                enableDots={true}
                dotSize={8}
                dotColor={{ from: 'color' }}
                dotBorderWidth={2}
                dotBorderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
                colors={['#9C4DF4']}
                fillOpacity={0.4}
                blendMode="normal"
                animate={true}
                motionConfig="gentle"
                theme={{
                  axis: {
                    domain: {
                      line: {
                        stroke: '#e9d5ff',
                        strokeWidth: 1
                      }
                    },
                    ticks: {
                      line: {
                        stroke: '#9ca3af',
                        strokeWidth: 1
                      },
                      text: {
                        fill: '#6b7280',
                        fontSize: 12,
                        fontWeight: 600
                      }
                    }
                  },
                  grid: {
                    line: {
                      stroke: '#e9d5ff',
                      strokeWidth: 1
                    }
                  }
                }}
              />
            </div>
          </div>
          
          {/* Orientation Scores with Speedometers */}
          <div className="orientations-section">
            <h3 className="section-title">Detailed Scores</h3>
            <div className="speedometers-grid">
              {Object.entries(result.scores).map(([orientation, score]) => {
                const normalizedScore = score / 100; // Gauge expects 0-1 range
                return (
                  <div key={orientation} className="speedometer-card">
                    <h4 className="speedometer-title">{formatOrientationName(orientation)}</h4>
                    <div id={`gauge-chart-container-${orientation}`} className="speedometer-container">
                      <GaugeChart
                        id={`gauge-${orientation}`}
                        percent={normalizedScore}
                        arcPadding={0.01}
                        arcWidth={0.25}
                        cornerRadius={2}
                        colors={['#ef4444', '#f59e0b', '#10b981']}
                        needleColor="#6b7280"
                        needleBaseColor="#6b7280"
                        textColor="#111827"
                        hideText={false}
                        animate={true}
                        animationDuration={2000}
                        formatTextValue={(value) => `${Math.round(score)}%`}
                        style={{ width: '100%', maxWidth: '100%' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Orientation Toxicity Breakdown */}
          {result.toxicityData && (
            <div className="orientations-section toxicity-orientations-section">
              <h3 className="section-title">Toxicity Breakdown by Orientation</h3>
              <p className="toxicity-description">
                Lower toxicity scores indicate healthier aspects of your culture.
              </p>
              <div className="toxicity-orientations-grid">
                {Object.entries(result.toxicityData.orientationToxicities).map(([orientation, toxicity]) => {
                  const toxicityPercentage = toxicity * 10;
                  return (
                    <div key={orientation} className="toxicity-orientation-card">
                      <div className="toxicity-orientation-header">
                        <h4 className="toxicity-orientation-name">{formatOrientationName(orientation)}</h4>
                        <span className="toxicity-orientation-score">{Math.round(toxicityPercentage)}%</span>
                      </div>
                      <div className="toxicity-bar-container">
                        <div 
                          className="toxicity-bar-fill" 
                          style={{ 
                            width: `${toxicityPercentage}%`,
                            backgroundColor: toxicity > 6 ? '#ef4444' : toxicity > 3 ? '#f59e0b' : '#10b981'
                          }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="culture-assessment">
      {/* Maturity Stage Question (Page 0) */}
      {currentPage === 0 && !showContactForm && (
        <>
          {maturityStageLoading ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>Loading organization types...</p>
            </div>
          ) : (
            <div className="question-card maturity-stage-question">
              <div className="question-header">
                <h3>Question 1</h3>
              </div>
              <p className="question-text">Our organization could be best described as:</p>
              
              <div className="maturity-options">
                {maturityStages.map(stage => (
                  <div
                    key={stage.code}
                    className={`maturity-option ${maturityStage === stage.code ? 'selected' : ''}`}
                    onClick={() => handleMaturityStageChange(stage.code)}
                    title={stage.description}
                  >
                    <div className="option-header">
                      <input
                        type="radio"
                        name="maturityStage"
                        value={stage.code}
                        checked={maturityStage === stage.code}
                        onChange={() => handleMaturityStageChange(stage.code)}
                        className="radio-input"
                      />
                      <span className="option-label">{stage.description}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Contact Form - email, first name, last name before results */}
      {showContactForm && (
        <div className="question-card contact-form-card">
          <div className="question-header">
            <h3>Almost there!</h3>
          </div>
          <p className="question-text">Please provide your details to view your personalized assessment results.</p>
          <div className="contact-form-fields">
            <div className="contact-form-row">
              <label htmlFor="contact-firstName">First Name *</label>
              <input
                id="contact-firstName"
                type="text"
                value={contactFormData.firstName}
                onChange={(e) => handleContactFormChange('firstName', e.target.value)}
                placeholder="Enter your first name"
                className="contact-form-input"
              />
            </div>
            <div className="contact-form-row">
              <label htmlFor="contact-lastName">Last Name</label>
              <input
                id="contact-lastName"
                type="text"
                value={contactFormData.lastName}
                onChange={(e) => handleContactFormChange('lastName', e.target.value)}
                placeholder="Enter your last name"
                className="contact-form-input"
              />
            </div>
            <div className="contact-form-row">
              <label htmlFor="contact-email">Email *</label>
              <input
                id="contact-email"
                type="email"
                value={contactFormData.email}
                onChange={(e) => handleContactFormChange('email', e.target.value)}
                placeholder="Enter your email"
                className="contact-form-input"
              />
            </div>
            {contactFormError && <p className="contact-form-error">{contactFormError}</p>}
          </div>
          <div className="contact-form-actions">
            <button
              type="button"
              onClick={() => setShowContactForm(false)}
              className="nav-button prev"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => handleSubmit(contactFormData)}
              disabled={submitting}
              className="nav-button submit"
            >
              {submitting ? 'Submitting...' : 'View Results'}
            </button>
          </div>
        </div>
      )}

      {/* Culture Questions (Pages 1+) */}
      {currentPage > 0 && !showContactForm && (
        <div className="questions-container">
          {getCurrentQuestions().map((question) => (
            <div key={question.id} className="question-card">
              <div className="question-header">
                <h3>Question {question.id}</h3>
              </div>
              
              <p className="question-text">{question.text}</p>
              
              <div className="rating-container">
                <div className="rating-labels">
                  <span>Strongly Disagree</span>
                  <span>Strongly Agree</span>
                </div>
                
                <div className="rating-numbers-container">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <button
                      key={num}
                      type="button"
                      className={`rating-number ${responses[question.id] === num ? 'selected' : ''}`}
                      onClick={() => handleScoreChange(question.id, num)}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!showContactForm && (
        <div className="navigation">
          <button 
            onClick={handlePrevious}
            disabled={currentPage === 0}
            className="nav-button prev"
          >
            Previous
          </button>
          
          {currentPage === totalPages ? (
            <button 
              onClick={handleShowContactForm}
              disabled={!isAllComplete()}
              className="nav-button submit"
            >
              Submit Assessment
            </button>
          ) : (
            <button 
              onClick={handleNext}
              disabled={!isCurrentPageComplete()}
              className="nav-button next"
            >
              Next
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CultureAssessment;
